/**
 * Suivi des arrêts de travail — Redux slice.
 *
 * Backs the "Work stoppages" sub-view of Leaves & Absences. `myList` holds a collab's own
 * arrêts (the self-service view, basics only); the rest is HR's suivi:
 *
 *   - `list`        the arrêts themselves, each already carrying its `payments` array and
 *                   the V-PLANNING context (entité, site, statut, ancienneté, sortie) the
 *                   API resolves as of the first day of the arrêt.
 *   - `reference`   the référentiel Syntec motifs, fetched once — it is a settings table,
 *                   not per-request data.
 *   - `eligibleLeaves` the planning leaves that may still be declared. Fetched only when the
 *                   form opens, because it is a scan of the planning and the answer changes
 *                   as soon as anything is declared.
 *
 * Dates arrive as `YYYY-MM-DD` strings and are kept as strings in the store. They are only
 * turned into `Date` objects where a component needs one (the grid's date columns), so a
 * timezone shift can never move an arrêt onto the previous day on its way through the store.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { store } from "../../app/store/store";
import { setIsLoading } from "../../containers/common/commonSlice";

/** "Statut" of an arrêt — the besoin's dropdown: Initial / Prolongation. */
export const STOPPAGE_STATUSES = ["INITIAL", "PROLONGATION"];

/** Where a reimbursement came from. */
export const PAYMENT_SOURCES = ["CPAM", "PREVOYANCE"];

/**
 * Where an organisme stands on reimbursing an arrêt — one status per organisme
 * (`cpam_reimbursement_status`, `prevoyance_reimbursement_status`), set by hand. Each value's
 * label is the `WORK_STOPPAGE_REIMBURSEMENT_<value>` translation key.
 */
export const REIMBURSEMENT_STATUSES = ["PENDING", "PARTIAL", "REIMBURSED", "REFUSED", "NOT_APPLICABLE"];

const initialState = {
    list: [],
    reference: [],
    eligibleLeaves: [],
    /** The arrêt open in the details drawer, with its history. */
    selected: null,
    selectedHistory: [],
    loadingList: false,
    loadingEligibleLeaves: false,
    /** Collabs' work-stoppage requests from Congés, awaiting HR validation here. */
    pendingLeaves: [],
    /** Justificatifs (PDF leave attachments) of the arrêt open in the form or the details. */
    attachments: [],
    attachmentsLoading: false,
    /** The signed-in collab's own arrêts and requests — each with a `locked` flag. */
    myList: [],
    loadingMyList: false,
};

export const WorkStoppagesSlice = createSlice({
    name: "workStoppages",
    initialState,
    reducers: {
        clearSelectedWorkStoppage: (state) => {
            state.selected = null;
            state.selectedHistory = [];
        },
        clearEligibleLeaves: (state) => {
            state.eligibleLeaves = [];
        },
        clearWorkStoppageAttachments: (state) => {
            state.attachments = [];
            state.attachmentsLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getWorkStoppages.pending, (state) => {
                state.loadingList = true;
            })
            .addCase(getWorkStoppages.fulfilled, (state, action) => {
                state.loadingList = false;
                const data = action.payload?.responseData;
                if (data && Array.isArray(data.list)) {
                    state.list = data.list.map((row) => ({
                        ...row,
                        payments: row.payments || [],
                    }));
                }
            })
            .addCase(getWorkStoppages.rejected, (state) => {
                state.loadingList = false;
            })
            .addCase(getWorkStoppageReference.fulfilled, (state, action) => {
                const data = action.payload?.responseData;
                if (data && Array.isArray(data.reference)) state.reference = data.reference;
            })
            .addCase(getEligibleLeaves.pending, (state) => {
                state.loadingEligibleLeaves = true;
            })
            .addCase(getEligibleLeaves.fulfilled, (state, action) => {
                state.loadingEligibleLeaves = false;
                const data = action.payload?.responseData;
                state.eligibleLeaves = data && Array.isArray(data.leaves) ? data.leaves : [];
            })
            .addCase(getEligibleLeaves.rejected, (state) => {
                state.loadingEligibleLeaves = false;
            })
            .addCase(getWorkStoppage.fulfilled, (state, action) => {
                const data = action.payload?.responseData;
                if (data && data.stoppage) {
                    state.selected = { ...data.stoppage, payments: data.stoppage.payments || [] };
                    state.selectedHistory = data.history || [];
                }
            })
            .addCase(getWorkStoppageHistory.fulfilled, (state, action) => {
                const data = action.payload?.responseData;
                if (data && Array.isArray(data.history)) state.selectedHistory = data.history;
            })
            .addCase(getPendingWorkStoppageLeaves.fulfilled, (state, action) => {
                const data = action.payload?.responseData;
                state.pendingLeaves = data && Array.isArray(data.leaves) ? data.leaves : [];
            })
            .addCase(getWorkStoppageAttachments.pending, (state) => {
                state.attachmentsLoading = true;
            })
            .addCase(getWorkStoppageAttachments.fulfilled, (state, action) => {
                state.attachmentsLoading = false;
                const data = action.payload?.responseData;
                state.attachments = data && data.status === 1 && Array.isArray(data.list) ? data.list : [];
            })
            .addCase(getWorkStoppageAttachments.rejected, (state) => {
                state.attachmentsLoading = false;
                state.attachments = [];
            })
            .addCase(getMyWorkStoppages.pending, (state) => {
                state.loadingMyList = true;
            })
            .addCase(getMyWorkStoppages.fulfilled, (state, action) => {
                state.loadingMyList = false;
                const data = action.payload?.responseData;
                state.myList = data && Array.isArray(data.list) ? data.list : [];
            })
            .addCase(getMyWorkStoppages.rejected, (state) => {
                state.loadingMyList = false;
            })
            // One arrêt's justificatifs are open at a time, so the collab's share HR's slot.
            .addCase(getMyWorkStoppageAttachments.pending, (state) => {
                state.attachmentsLoading = true;
            })
            .addCase(getMyWorkStoppageAttachments.fulfilled, (state, action) => {
                state.attachmentsLoading = false;
                const data = action.payload?.responseData;
                state.attachments = data && data.status === 1 && Array.isArray(data.list) ? data.list : [];
            })
            .addCase(getMyWorkStoppageAttachments.rejected, (state) => {
                state.attachmentsLoading = false;
                state.attachments = [];
            });
    },
});

/**
 * The thunks below all return the raw `{responseData}` envelope rather than throwing, which
 * is this codebase's convention: the caller reads `responseData.status` and decides which
 * toast to show. `setIsLoading` brackets each call so the global spinner behaves as it does
 * everywhere else.
 */
const post = async (path, body) => {
    store.dispatch(setIsLoading(true));
    try {
        return await NetworkManager.getInstance().postRequest(path, body);
    } catch (error) {
        return error;
    } finally {
        store.dispatch(setIsLoading(false));
    }
};

export const getWorkStoppages = createAsyncThunk(
    "workStoppages/getAll",
    async (body) => post(config.apiPaths.workStoppages.getAll, body || {})
);

export const getWorkStoppage = createAsyncThunk(
    "workStoppages/get",
    async (body) => post(config.apiPaths.workStoppages.get, body)
);

export const getWorkStoppageHistory = createAsyncThunk(
    "workStoppages/getHistory",
    async (body) => post(config.apiPaths.workStoppages.getHistory, body)
);

export const getEligibleLeaves = createAsyncThunk(
    "workStoppages/getEligibleLeaves",
    async (body) => post(config.apiPaths.workStoppages.getEligibleLeaves, body || {})
);

export const addWorkStoppage = createAsyncThunk(
    "workStoppages/add",
    async (body) => post(config.apiPaths.workStoppages.add, body)
);

export const editWorkStoppage = createAsyncThunk(
    "workStoppages/edit",
    async (body) => post(config.apiPaths.workStoppages.edit, body)
);

export const deleteWorkStoppage = createAsyncThunk(
    "workStoppages/delete",
    async (body) => post(config.apiPaths.workStoppages.delete, body)
);

export const addWorkStoppagePayment = createAsyncThunk(
    "workStoppages/addPayment",
    async (body) => post(config.apiPaths.workStoppages.addPayment, body)
);

export const editWorkStoppagePayment = createAsyncThunk(
    "workStoppages/editPayment",
    async (body) => post(config.apiPaths.workStoppages.editPayment, body)
);

export const deleteWorkStoppagePayment = createAsyncThunk(
    "workStoppages/deletePayment",
    async (body) => post(config.apiPaths.workStoppages.deletePayment, body)
);

/**
 * The référentiel is a GET, and it is the one call that is not per-request data: fetched
 * once when the sub-view is first opened and kept for the session.
 */
export const getWorkStoppageReference = createAsyncThunk(
    "workStoppages/getReference",
    async (params) => {
        try {
            return await NetworkManager.getInstance().getRequest(
                config.apiPaths.workStoppages.getReference,
                params || {}
            );
        } catch (error) {
            return error;
        }
    }
);

/**
 * The "demandes à valider" panel: pending requests collabs made from Congés for a
 * work-stoppage leave type. Validating one is declaring its suivi; see rejectWorkStoppageLeave
 * for the other answer.
 */
export const getPendingWorkStoppageLeaves = createAsyncThunk(
    "workStoppages/getPendingLeaves",
    async () => post(config.apiPaths.workStoppages.getEligibleLeaves, { only_pending: 1 })
);

export const rejectWorkStoppageLeave = createAsyncThunk(
    "workStoppages/rejectLeave",
    async (body) => post(config.apiPaths.workStoppages.rejectLeave, body)
);

/** Justificatifs: PDF leave attachments on the arrêt's planning leave. */
export const getWorkStoppageAttachments = createAsyncThunk(
    "workStoppages/getAttachments",
    async (idPlanning) => post(config.apiPaths.workStoppages.listAttachments, { id_planning: idPlanning })
);

/**
 * Multipart, so it goes through NetworkManager's dedicated uploader rather than `post`. The
 * result is wrapped as `{responseData}` so callers read it like every other thunk here.
 */
export const uploadWorkStoppageAttachment = createAsyncThunk(
    "workStoppages/uploadAttachment",
    async ({ file, idPlanning }) => {
        try {
            const data = await NetworkManager.getInstance().uploadWorkStoppageAttachment(file, idPlanning);
            return { responseData: data };
        } catch (error) {
            return { responseData: error?.response?.data || { status: 0 } };
        }
    }
);

export const deleteWorkStoppageAttachment = createAsyncThunk(
    "workStoppages/deleteAttachment",
    async (body) => post(config.apiPaths.workStoppages.deleteAttachment, body)
);

/**
 * Self-service: the signed-in collab's own arrêts. No scope on the API side — every call is
 * confined to the caller's own leaves, and every write is refused (`locked: 1`) once HR has
 * validated or refused the arrêt.
 */
export const getMyWorkStoppages = createAsyncThunk(
    "workStoppages/getMine",
    async () => post(config.apiPaths.workStoppages.getMine, {})
);

export const requestMyWorkStoppage = createAsyncThunk(
    "workStoppages/requestMine",
    async (body) => post(config.apiPaths.workStoppages.requestMine, body)
);

export const editMyWorkStoppage = createAsyncThunk(
    "workStoppages/editMine",
    async (body) => post(config.apiPaths.workStoppages.editMine, body)
);

export const cancelMyWorkStoppage = createAsyncThunk(
    "workStoppages/cancelMine",
    async (body) => post(config.apiPaths.workStoppages.cancelMine, body)
);

export const getMyWorkStoppageAttachments = createAsyncThunk(
    "workStoppages/getMyAttachments",
    async (idPlanning) => post(config.apiPaths.workStoppages.listMyAttachments, { id_planning: idPlanning })
);

export const uploadMyWorkStoppageAttachment = createAsyncThunk(
    "workStoppages/uploadMyAttachment",
    async ({ file, idPlanning }) => {
        try {
            const data = await NetworkManager.getInstance().uploadMyWorkStoppageAttachment(file, idPlanning);
            return { responseData: data };
        } catch (error) {
            return { responseData: error?.response?.data || { status: 0 } };
        }
    }
);

export const deleteMyWorkStoppageAttachment = createAsyncThunk(
    "workStoppages/deleteMyAttachment",
    async (body) => post(config.apiPaths.workStoppages.deleteMyAttachment, body)
);

export const {
    clearSelectedWorkStoppage, clearEligibleLeaves, clearWorkStoppageAttachments,
} = WorkStoppagesSlice.actions;

export default WorkStoppagesSlice.reducer;
